import { cloneDeep, snakeCase } from 'lodash-es';
import { BaseEntityDescriptor, } from '../core/BaseEntityDescriptor.js';
import { removeUndefined } from '../utils/objects.js';
import { indent, pascalCase, wrapComment } from '../utils/strings.js';
import { BaseMessage } from './BaseMessage.js';
const MESSAGE_METADATA_SYMBOL = Symbol('message:metadata');
const populateTemplate = (template, name) => template
    .replace(/\[\[name]]/g, snakeCase(name))
    .replace(/\[\[name.pc]]/g, pascalCase(name));
class MessageDescriptor extends BaseEntityDescriptor {
    OwnerClass;
    messageMetadata;
    fieldsMetadata = {};
    example = {};
    constructor(target, stored) {
        super(target.name);
        this.OwnerClass = target;
        if (stored) {
            this.messageMetadata = cloneDeep(stored.messageMetadata);
            this.fieldsMetadata = { ...stored.fieldsMetadata };
        }
        this.messageMetadata = {
            ...this.messageMetadata,
            messageName: this.OwnerClass.name,
        };
    }
    addMessageMetadata(meta) {
        this.messageMetadata = {
            ...this.messageMetadata,
            ...removeUndefined(meta),
        };
        this.save();
    }
    addFieldMetadata(field, meta) {
        this.fieldsMetadata[field] = meta;
        this.save();
    }
    save() {
        Reflect.defineMetadata(MESSAGE_METADATA_SYMBOL, this, this.OwnerClass.prototype);
    }
    getExample(transport) {
        if (!this.example[transport]) {
            this.example[transport] = {};
            const fieldSchemas = Object.entries(this.fieldsMetadata);
            this.example[transport] = removeUndefined(fieldSchemas.reduce((map, [fieldName, field]) => {
                map[fieldName] = field.getExample(transport);
                return map;
            }, {}));
        }
        return this.example[transport];
    }
    toSchema(transport, options) {
        const fieldSchemas = Object.entries(this.fieldsMetadata);
        const properties = fieldSchemas.reduce((map, [fieldName, field]) => {
            const fieldSchema = field.toSchema(transport, options);
            if (!fieldSchema) {
                return map;
            }
            map[fieldName] = fieldSchema;
            return map;
        }, {});
        const required = fieldSchemas
            .filter(([fieldName, field]) => properties[fieldName] && field.required)
            .map(([fieldName]) => fieldName);
        const references = fieldSchemas.map(([, field]) => field.references).flat();
        const schema = {
            type: 'object',
            properties,
            example: this.getExample(transport),
            ...(required.length && { required }),
        };
        return { schema, references };
    }
    toProto(idMappings) {
        const name = this.OwnerClass.name;
        const mapping = idMappings[name] || { __type: 'message', __next: 1 };
        const fieldSchemas = Object.entries(this.fieldsMetadata).filter(([, field]) => field.proto.type);
        const definitionBody = fieldSchemas
            .map(([fieldName, field]) => [
            ...field.proto.comments.map(wrapComment).flat(),
            ...field.proto.definition.map((line) => populateTemplate(line, fieldName)),
            `${populateTemplate(field.proto.type, fieldName)} ${snakeCase(fieldName)} = ${(mapping[snakeCase(fieldName)] = mapping[snakeCase(fieldName)] ?? mapping.__next++).toString()};`,
        ])
            .flat()
            .map(indent);
        idMappings[name] = mapping;
        const proto = {
            comments: [],
            definition: [
                '',
                ...wrapComment(this.messageMetadata.description),
                `message ${name} {`,
                ...definitionBody,
                '}',
            ],
        };
        const references = fieldSchemas.map(([, field]) => field.references).flat();
        return { proto, references };
    }
    static load(target) {
        const targetProto = target instanceof BaseMessage ? target : target.prototype;
        const targetCtor = target instanceof BaseMessage ? target.constructor : target;
        const stored = Reflect.getMetadata(MESSAGE_METADATA_SYMBOL, targetProto);
        if (!stored || stored.OwnerClass !== targetCtor) {
            return new MessageDescriptor(targetCtor, stored);
        }
        else {
            return stored;
        }
    }
}
export default MessageDescriptor;
//# sourceMappingURL=MessageDescriptor.js.map